// Silero VAD (@ricky0123/vad-web): a trained classifier, robust to raw
// hardware gain where the RMS detector in micStream.js misfires.
// Never opens a mic: it consumes micStream's stream and context, since a second
// capture would break the iOS audio-session fix. Every load failure resolves
// to false so micStream falls back to RMS.

// Pinned: asset filenames and ms-based option names are release specific.
const ORT_VERSION = '1.22.0'
// >= 0.0.30 required: 0.0.29 never assigns a caller-supplied audioContext, so start() always threw.
const VAD_VERSION = '0.0.30'
const ORT_BASE = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@' + ORT_VERSION + '/dist/'
const VAD_BASE = 'https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@' + VAD_VERSION + '/dist/'
const ORT_URL = ORT_BASE + 'ort.wasm.min.js'
const VAD_URL = VAD_BASE + 'bundle.min.js'

// Library default 1400; Silero is accurate enough for a short tail (RMS path needs 1800).
export const SILERO_REDEMPTION_MS = 700

const MIN_SPEECH_MS = 300

// Pads Silero's internal segment only; our MediaRecorder clip is unaffected.
const PRE_SPEECH_PAD_MS = 800

const POSITIVE_SPEECH_THRESHOLD = 0.3
const NEGATIVE_SPEECH_THRESHOLD = 0.25

let loadPromise = null
let instance = null

// Latched after MicVAD.new() throws once: it is an environment verdict (on
// iPhone the wasm backend OOMs), and each retry re-fetches the model and
// stalls the main thread.
let constructionFailed = false

// Lets a load that resolves after the mic closed destroy itself.
let generation = 0

export function isSupported () {
  return !!(
    typeof window !== 'undefined' &&
    typeof document !== 'undefined' &&
    document.head &&
    typeof WebAssembly !== 'undefined'
  )
}

export function isActive () {
  return !!instance
}

function loadScript (url) {
  return new Promise(function (resolve, reject) {
    const existing = document.querySelector('script[data-vad-src="' + url + '"]')
    if (existing) {
      if (existing.getAttribute('data-vad-loaded') === '1') {
        resolve()
        return
      }
      existing.addEventListener('load', function () { resolve() })
      existing.addEventListener('error', function () { reject(new Error('vad: failed to load ' + url)) })
      return
    }

    const el = document.createElement('script')
    el.src = url
    el.async = true
    el.setAttribute('data-vad-src', url)
    el.onload = function () {
      el.setAttribute('data-vad-loaded', '1')
      resolve()
    }
    el.onerror = function () {
      reject(new Error('vad: failed to load ' + url))
    }
    document.head.appendChild(el)
  })
}

// Result, including failure, is cached so a dead CDN can't re-stall every mic open.
export function load () {
  if (loadPromise) return loadPromise
  if (!isSupported()) {
    loadPromise = Promise.resolve(false)
    return loadPromise
  }

  // ort must be global first: the VAD UMD bundle reads it from global scope.
  loadPromise = loadScript(ORT_URL)
    .then(function () { return loadScript(VAD_URL) })
    .then(function () {
      return !!(window.vad && window.vad.MicVAD && typeof window.vad.MicVAD.new === 'function')
    })
    .catch(function (e) {
      console.warn('[vadSilero] unavailable, falling back to RMS:', e && e.message)
      return false
    })

  return loadPromise
}

// Start on an ALREADY-OPEN stream/context. false means the caller keeps RMS.
export async function attach (options) {
  const opts = options || {}
  if (!opts.audioContext || !opts.stream) return false
  if (constructionFailed) return false

  const ready = await load()
  if (!ready) return false

  await detach()
  const myGeneration = ++generation

  const stream = opts.stream
  let built
  try {
    built = await window.vad.MicVAD.new({
      // Inert pause/resume: micStream owns the stream's lifetime (iOS release path).
      audioContext: opts.audioContext,
      getStream: function () { return Promise.resolve(stream) },
      pauseStream: function () { return Promise.resolve() },
      resumeStream: function () { return Promise.resolve(stream) },
      startOnLoad: false,
      submitUserSpeechOnPause: false,

      baseAssetPath: VAD_BASE,
      onnxWASMBasePath: ORT_BASE,
      processorType: 'auto',

      positiveSpeechThreshold: POSITIVE_SPEECH_THRESHOLD,
      negativeSpeechThreshold: NEGATIVE_SPEECH_THRESHOLD,
      redemptionMs: typeof opts.redemptionMs === 'number' ? opts.redemptionMs : SILERO_REDEMPTION_MS,
      minSpeechMs: MIN_SPEECH_MS,
      preSpeechPadMs: PRE_SPEECH_PAD_MS,

      onSpeechStart: function () {
        if (myGeneration !== generation) return
        if (typeof opts.onSpeechStart === 'function') opts.onSpeechStart()
      },
      // Segment audio ignored: micStream's MediaRecorder clip is what gets scored.
      onSpeechEnd: function () {
        if (myGeneration !== generation) return
        if (typeof opts.onSpeechEnd === 'function') opts.onSpeechEnd()
      },
      onVADMisfire: function () {}
    })
  } catch (e) {
    // start() failing below is NOT latched: that one is about this stream.
    constructionFailed = true
    console.warn('[vadSilero] init failed, falling back to RMS for the rest of this session:', e && e.message)
    return false
  }

  if (myGeneration !== generation) {
    try { built.destroy() } catch (e) { /* best effort */ }
    return false
  }

  instance = built
  try {
    await instance.start()
  } catch (e) {
    console.warn('[vadSilero] start failed, falling back to RMS:', e && e.message)
    await detach()
    return false
  }

  if (myGeneration !== generation) {
    await detach()
    return false
  }

  return true
}

// Never touches the stream or context: micStream owns both (iOS release order).
export function detach () {
  generation++
  const active = instance
  instance = null
  if (!active) return Promise.resolve()

  try {
    // pause() first so no callback fires mid-teardown.
    const paused = active.pause()
    const done = paused && typeof paused.then === 'function' ? paused : Promise.resolve()
    return done
      .catch(function () {})
      .then(function () {
        try { active.destroy() } catch (e) { /* best effort */ }
      })
  } catch (e) {
    try { active.destroy() } catch (e2) { /* best effort */ }
    return Promise.resolve()
  }
}

export function __resetForTests () {
  loadPromise = null
  instance = null
  generation = 0
  constructionFailed = false
}
